import { Grid2 as Grid, LinearProgress, Paper, Typography } from "@mui/material";
import useScreenSize from "../../../../hooks/useScreenSize";
import { Country } from "country-state-city";
import { Apple, Android } from "@mui/icons-material";
import { formatNumber } from "../../../../hooks/formatNumber";

type CountryPlatformProps = {
    countryData: {[key: string]: number},
    platformData: {[key: string]: number},
    size?: number
}

type SectionProps = { data: {[key: string]: number}, screenSize: { width: number; height: number; } }

const CountryPlatformWidget = ({ countryData = {}, platformData = {}, size = 12 }: CountryPlatformProps) => {
    const screenSize = useScreenSize();

    return (
        <Grid size={screenSize.width < 1200 ? 12 : size}>
            <Paper elevation={5} sx={{ 
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}>
                <Grid container spacing={5}>
                    <CountrySection data={countryData} screenSize={screenSize} />
                    <PlatformSection data={platformData} screenSize={screenSize} />
                </Grid>
            </Paper>
        </Grid>
    )
}

const CountrySection = ({ data = {}, screenSize }: SectionProps) => {
    const totalVisits = Object.values(data).reduce((s, visits) => s = s + visits, 0);
    function getAverage(num: number) {
        return totalVisits === 0 ? 0 : Math.round((num * 100 / totalVisits * 100) + Number.EPSILON) / 100;
    }

    return (
        <Grid size={screenSize.width < 1200 ? 12 : 6} display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Impressions by Country</Typography>
            {totalVisits === 0 ? (
                <Typography>
                    No Country data found
                </Typography>
            ) : (
                Object.keys(data).map((code) => {
                    return <div key={code}>
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid height={31} size={1} sx={{display: 'flex', alignItems: 'center' }}>
                                <img
                                    loading="lazy"
                                    srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
                                    src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
                                    alt=""
                                />
                            </Grid>
                            <Grid size={5}>
                                <Typography>{Country.getCountryByCode(code)?.name}</Typography>
                            </Grid>
                            <Grid size={1}>
                                <Typography>{formatNumber(data[code])}</Typography>
                            </Grid>
                            <Grid size={5}>
                                <LinearProgress variant="determinate" value={getAverage(data[code])} />
                            </Grid>
                        </Grid>
                    </div>
                })
            )}
        </Grid>
    )
}

const PlatformSection = ({ data = {}, screenSize }: SectionProps) => {
    const totalVisits = Object.values(data).reduce((s, visits) => s = s + visits, 0);
    function getAverage(num: number) {
        return totalVisits === 0 ? 0 : Math.round((num * 100 / totalVisits * 100) + Number.EPSILON) / 100;
    }

    return (
        <Grid size={screenSize.width < 1200 ? 12 : 6} display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Impressions by Platform</Typography>
            {totalVisits === 0 ? (
                <Typography>
                    No Platform data found
                </Typography>
            ) : (
                Object.keys(data).map((platform) => {
                    return <div key={platform}>
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid height={31} size={1} sx={{display: 'flex', alignItems: 'center' }}>
                                {platform === "iOS" ? <Apple /> : <Android />}
                            </Grid>
                            <Grid size={5}>
                                <Typography>{platform}</Typography>
                            </Grid>
                            <Grid size={1}>
                                <Typography>{formatNumber(data[platform])}</Typography>
                            </Grid>
                            <Grid size={5}>
                                <LinearProgress variant="determinate" value={getAverage(data[platform])} />
                            </Grid>
                        </Grid>
                    </div>
                })
            )}
        </Grid>
    )
}

export default CountryPlatformWidget;
